import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart3, TrendingUp, Users, UserCheck,
  FileText, Activity, Network, Zap, Volume2
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserPartner } from '../../lib/auth';
import { loadTeams, loadTeamResults, computeTeamAverages } from '../../lib/teams';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { characteristics } from '../../data/characteristics';
import type { TeamAverageScores } from '../../types/team';
import { NlpPracticeUsagePanel } from '../../components/partner/NlpPracticeUsagePanel';

interface AnalyticsData {
  totalCoaches: number;
  totalRespondents: number;
  totalAssessments: number;
  totalTeams: number;
  codesUsed: number;
  codesAvailable: number;
  recentAssessments: { date: string; count: number }[];
  teamAverages: TeamAverageScores[];
  coachAiUsage: { coachId: string; coachName: string; tokens: number; voiceMinutes: number; activeClients: number }[];
}

export function PartnerAnalytics() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalCoaches: 0,
    totalRespondents: 0,
    totalAssessments: 0,
    totalTeams: 0,
    codesUsed: 0,
    codesAvailable: 0,
    recentAssessments: [],
    teamAverages: [],
    coachAiUsage: [],
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    const init = async () => {
      try {
        const isPartner = await isUserPartner(user.id);
        if (!isPartner) {
          navigate('/login');
          return;
        }
        await loadAnalytics(user.id);
      } catch (err) {
        console.error('Error checking partner status:', err);
        navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const loadAnalytics = async (userId: string) => {
    try {
      const { data: partner } = await supabase
        .from('partners')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (!partner) return;

      const { data: coaches } = await supabase
        .from('coaches')
        .select('id, user_id, users!coaches_user_id_fkey(full_name, email)')
        .eq('partner_id', partner.id);

      const coachIds = (coaches || []).map(c => c.id);

      let totalRespondents = 0;
      let totalAssessments = 0;
      const last7Days: Record<string, number> = {};
      const today = new Date();
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        last7Days[date.toISOString().split('T')[0]] = 0;
      }

      if (coachIds.length > 0) {
        const { count: respondentCount } = await supabase
          .from('respondents')
          .select('id', { count: 'exact', head: true })
          .in('coach_id', coachIds);
        totalRespondents = respondentCount || 0;

        const { data: results } = await supabase
          .from('results')
          .select('completed_at')
          .in('coach_id', coachIds);
        totalAssessments = (results || []).length;

        (results || []).forEach(result => {
          if (result.completed_at) {
            const dateKey = new Date(result.completed_at).toISOString().split('T')[0];
            if (last7Days.hasOwnProperty(dateKey)) {
              last7Days[dateKey]++;
            }
          }
        });
      }

      const { data: codes } = await supabase
        .from('assessment_codes')
        .select('is_used')
        .eq('partner_id', partner.id);

      const codesUsed = (codes || []).filter(c => c.is_used).length;
      const codesAvailable = (codes || []).filter(c => !c.is_used).length;

      const teams = await loadTeams(partner.id);
      const teamAverages: TeamAverageScores[] = [];
      for (const team of teams.filter(t => t.type === 'team')) {
        const results = await loadTeamResults(team.id);
        if (results.length > 0) {
          teamAverages.push(computeTeamAverages(team.id, team.name, results));
        }
      }

      setAnalytics({
        totalCoaches: (coaches || []).length,
        totalRespondents,
        totalAssessments,
        totalTeams: teams.length,
        codesUsed,
        codesAvailable,
        recentAssessments: Object.entries(last7Days).map(([date, count]) => ({ date, count })),
        teamAverages,
        coachAiUsage: [],
      });

      // Load AI usage per coach (month to date)
      if (coachIds.length > 0) {
        const monthStart = new Date();
        monthStart.setDate(1);
        monthStart.setHours(0, 0, 0, 0);

        const { data: aiLogs } = await (supabase
          .from('ai_coach_usage_logs')
          .select('coach_id, type, duration_seconds, token_input, token_output, respondent_id')
          .in('coach_id', coachIds)
          .gte('created_at', monthStart.toISOString()) as unknown as Promise<{ data: Array<{ coach_id: string; type: string; duration_seconds: number | null; token_input: number | null; token_output: number | null; respondent_id: string }> | null; error: unknown }>);

        const coachUsageMap = new Map<string, { tokens: number; voiceSeconds: number; clients: Set<string> }>();
        (aiLogs || []).forEach(log => {
          const entry = coachUsageMap.get(log.coach_id) || { tokens: 0, voiceSeconds: 0, clients: new Set() };
          if (log.type === 'chat') {
            entry.tokens += (log.token_input || 0) + (log.token_output || 0);
          } else {
            entry.voiceSeconds += log.duration_seconds || 0;
          }
          entry.clients.add(log.respondent_id);
          coachUsageMap.set(log.coach_id, entry);
        });

        const coachAiUsage = (coaches || [])
          .map(c => {
            const u = coachUsageMap.get(c.id);
            return {
              coachId: c.id,
              coachName: (c.users as any)?.full_name || (c.users as any)?.email || 'Unknown',
              tokens: u?.tokens || 0,
              voiceMinutes: Math.round((u?.voiceSeconds || 0) / 60),
              activeClients: u?.clients.size || 0,
            };
          })
          .sort((a, b) => b.tokens - a.tokens);

        setAnalytics(prev => ({ ...prev, coachAiUsage }));
      }
    } catch (err) {
      console.error('Error loading analytics:', err);
    }
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Loading analytics..." />;
  }

  const statCards = [
    { label: 'Total Coaches', value: analytics.totalCoaches, icon: UserCheck, color: 'bg-teal-500' },
    { label: 'Total Respondents', value: analytics.totalRespondents, icon: Users, color: 'bg-blue-500' },
    { label: 'Teams', value: analytics.totalTeams, icon: Network, color: 'bg-amber-500' },
    { label: 'Completed Assessments', value: analytics.totalAssessments, icon: FileText, color: 'bg-emerald-500' },
    { label: 'Codes Available', value: analytics.codesAvailable, icon: FileText, color: 'bg-rose-500' }
  ];

  const maxAssessments = Math.max(...analytics.recentAssessments.map(d => d.count), 1);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-xl">
              <BarChart3 className="w-6 h-6 text-teal-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Partner Analytics</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 ${stat.color} rounded-xl`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <div className="text-3xl font-bold text-slate-900 mb-1">{stat.value}</div>
              <div className="text-slate-500">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
          >
            <div className="flex items-center gap-2 mb-6">
              <Activity className="w-5 h-5 text-teal-600" />
              <h2 className="text-lg font-semibold text-slate-900">Assessments (Last 7 Days)</h2>
            </div>
            <div className="space-y-3">
              {analytics.recentAssessments.map((day) => (
                <div key={day.date} className="flex items-center gap-4">
                  <div className="w-20 text-sm text-slate-600">
                    {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </div>
                  <div className="flex-1 h-8 bg-slate-100 rounded-lg overflow-hidden">
                    <div
                      className="h-full bg-teal-500 rounded-lg transition-all"
                      style={{ width: `${(day.count / maxAssessments) * 100}%` }}
                    />
                  </div>
                  <div className="w-8 text-sm font-medium text-slate-900 text-right">
                    {day.count}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200"
          >
            <div className="flex items-center gap-2 mb-6">
              <FileText className="w-5 h-5 text-teal-600" />
              <h2 className="text-lg font-semibold text-slate-900">Code Usage</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-xl">
                <div className="text-3xl font-bold text-green-700">{analytics.codesUsed}</div>
                <div className="text-green-600">Codes Used</div>
              </div>
              <div className="p-4 bg-amber-50 rounded-xl">
                <div className="text-3xl font-bold text-amber-700">{analytics.codesAvailable}</div>
                <div className="text-amber-600">Codes Available</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-600">Usage Rate</span>
                <span className="font-medium text-slate-900">
                  {analytics.codesUsed + analytics.codesAvailable > 0
                    ? Math.round((analytics.codesUsed / (analytics.codesUsed + analytics.codesAvailable)) * 100)
                    : 0}%
                </span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-teal-500 rounded-full transition-all"
                  style={{
                    width: `${analytics.codesUsed + analytics.codesAvailable > 0
                      ? (analytics.codesUsed / (analytics.codesUsed + analytics.codesAvailable)) * 100
                      : 0}%`
                  }}
                />
              </div>
            </div>
          </motion.div>
        </div>

        {analytics.teamAverages.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 mt-6"
          >
            <div className="flex items-center gap-2 mb-6">
              <Network className="w-5 h-5 text-teal-600" />
              <h2 className="text-lg font-semibold text-slate-900">Team Performance Comparison</h2>
            </div>
            <p className="text-sm text-slate-500 mb-6">
              Average characteristic scores across teams ({analytics.teamAverages.length} team{analytics.teamAverages.length !== 1 ? 's' : ''} with results)
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Characteristic</th>
                    {analytics.teamAverages.map(ta => (
                      <th key={ta.teamId} className="text-center py-3 px-4 font-semibold text-slate-700">
                        {ta.teamName}
                        <div className="text-xs font-normal text-slate-400">{ta.membersWithResults} members</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {characteristics.map((char) => (
                    <tr key={char.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium text-slate-800">{char.name}</td>
                      {analytics.teamAverages.map(ta => {
                        const score = ta.averageScores.find(s => s.characteristicId === char.id);
                        const value = score ? Math.round(score.normalized) : 0;
                        return (
                          <td key={ta.teamId} className="py-3 px-4 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-teal-500 rounded-full"
                                  style={{ width: `${value}%` }}
                                />
                              </div>
                              <span className="font-medium text-slate-700 w-8 text-right">{value}</span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {analytics.coachAiUsage.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 mt-6"
          >
            <div className="flex items-center gap-2 mb-6">
              <Zap className="w-5 h-5 text-teal-600" />
              <h2 className="text-lg font-semibold text-slate-900">AI Coach Usage This Month</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Coach</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Tokens Used</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Voice Minutes</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Active Clients</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.coachAiUsage.map(row => (
                    <tr key={row.coachId} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium text-slate-800">{row.coachName}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Zap className="w-3.5 h-3.5 text-teal-500" />
                          <span className="font-medium text-slate-800">{row.tokens.toLocaleString()}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Volume2 className="w-3.5 h-3.5 text-amber-500" />
                          <span className="font-medium text-slate-800">{row.voiceMinutes}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="font-medium text-slate-800">{row.activeClients}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        <NlpPracticeUsagePanel />
      </main>
    </div>
  );
}
